// UI-related functions and event handlers
import { logMessage } from './utils.mjs';
import { renderReceiptView } from './receipt.mjs';
import { STORE_LINKS } from './config.mjs';
import t, { locale as i18nLocale } from 'virtual:i18n';

// Reset UI connection state
export function resetConnectionState() {
    const connectBtn = document.getElementById('connectBtn');
    const disconnectBtn = document.getElementById('disconnectBtn');

    connectBtn.disabled = false;
    disconnectBtn.disabled = true;
}

// Display message when no group is provided
export function displayNoGroupMessage() {
    // Disable connect button
    const connectBtn = document.getElementById('connectBtn');
    connectBtn.disabled = true;

    // Add a prominent message at the top
    const messagesEl = document.getElementById('messages');
    const msgEl = document.createElement('div');
    msgEl.className = 'message error';
    msgEl.style.fontWeight = 'bold';
    msgEl.style.fontSize = '1.2em';
    msgEl.style.padding = '10px';
    msgEl.style.marginBottom = '10px';
    msgEl.textContent = 'No group provided in URL';
    messagesEl.prepend(msgEl);
}

// Update UI with group data
export function updateUIWithGroupData(group) {
    if (!group) return;

    // Set the group ID in the input field
    const groupIdEl = document.getElementById('groupId');
    groupIdEl.value = group.id;

    // Log group information
    logMessage(`Group loaded: ${group.name}`, 'success');
    logMessage(`Currency: ${group.currency}`, 'info');
    logMessage(`Participants: ${group.participants.join(', ')}`, 'info');

    // Render expenses if available
    if (group.expenses && group.expenses.length > 0) {
        renderExpenseCards(group.expenses, group.participants);
    } else {
        logMessage('No expenses found for this group', 'info');
    }
}

// Initialize UI event listeners
export function initializeUI() {
    const subscriptionTypeEl = document.getElementById('subscriptionType');
    const groupIdContainerEl = document.getElementById('groupIdContainer');

}



// Render App Store and Play Store badges or smart mobile CTA
export function renderStoreBadges(parentEl) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);

    const storeLinks = STORE_LINKS;

    // Handler for download click - checks if user is a creator first
    const handleDownloadClick = async (e) => {
        e.preventDefault();

        const { trackEvent } = await import('./utils.mjs');
        trackEvent('like_splitpal_download');

        // Dynamically import the creator handoff module
        const { isCreator, startCreatorHandoff } = await import('./creatorHandoff.mjs');

        if (isCreator()) {
            // User has local groups - start handoff flow
            startCreatorHandoff();
        } else {
            // User is just a joiner/viewer - go straight to store
            const targetUrl = isIOS ? storeLinks.ios : storeLinks.android;
            window.open(targetUrl, '_blank');
        }
    };

    if (isIOS || isAndroid) {
        // Render a subtle, elegant link for mobile
        const mobileContainer = document.createElement('div');
        mobileContainer.className = 'mobile-get-app-container';

        const ctaLink = document.createElement('a');
        ctaLink.href = '#'; // Prevent default navigation
        ctaLink.className = 'mobile-cta-link';
        ctaLink.innerHTML = `
            Like this? <span>Get the App!</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
        `;

        // Add click handler
        ctaLink.addEventListener('click', handleDownloadClick);

        mobileContainer.appendChild(ctaLink);
        parentEl.appendChild(mobileContainer);
    } else {
        // Desktop or undetected: Show both badges
        const badgesContainer = document.createElement('div');
        badgesContainer.className = 'store-badges';

        const appStore = document.createElement('a');
        appStore.href = '#';
        appStore.className = 'store-badge';
        appStore.innerHTML = `<img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store">`;
        appStore.addEventListener('click', handleDownloadClick);

        const playStore = document.createElement('a');
        playStore.href = '#';
        playStore.className = 'store-badge';
        playStore.innerHTML = `<img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play">`;
        playStore.addEventListener('click', handleDownloadClick);

        badgesContainer.appendChild(appStore);
        badgesContainer.appendChild(playStore);
        parentEl.appendChild(badgesContainer);
    }
}

/**
 * Renders a premium custom language picker
 * @param {HTMLElement} parentEl - Parent element to append to
 * @param {'full' | 'header'} mode - Display mode
 */
export function renderLanguagePicker(parentEl, mode = 'full') {
    const locales = [
        { id: 'en', label: 'English' },
        { id: 'ru', label: 'Русский' },
        { id: 'es', label: 'Español' },
        { id: 'zh', label: '中文' },
        { id: 'id', label: 'Bahasa Indonesia' }
    ];

    const shortLocale = i18nLocale.split('-')[0];
    const currentLoc = locales.find(l => l.id === shortLocale) || locales[0];

    const container = document.createElement('div');
    container.className = `custom-lang-dropdown ${mode}-mode`;

    // Trigger (The visible part)
    const trigger = document.createElement('div');
    trigger.className = 'lang-dropdown-trigger';
    
    if (mode === 'header') {
        trigger.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="globe-icon">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="chevron-icon">
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        `;
        trigger.style.padding = '4px';
        trigger.style.gap = '4px';
    } else {
        trigger.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="globe-icon">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
            <span class="current-lang-label">${currentLoc.label}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="chevron-icon">
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        `;
    }

    // Menu (The hidden part without Flags)
    const menu = document.createElement('div');
    menu.className = 'lang-dropdown-menu';

    locales.forEach(loc => {
        const item = document.createElement('div');
        item.className = `lang-dropdown-item ${shortLocale === loc.id ? 'active' : ''}`;
        item.innerHTML = `<span class="item-label">${loc.label}</span>`;
        
        item.onclick = (e) => {
            e.stopPropagation();
            if (shortLocale === loc.id) {
                container.classList.remove('open');
                return;
            }

            // Redirection logic
            let path = window.location.pathname;
            const localesToStrip = ['/ru', '/es', '/zh', '/id'];
            for (const prefix of localesToStrip) {
                if (path.startsWith(prefix + '/') || path === prefix) {
                    path = path.substring(prefix.length);
                    break;
                }
            }
            const newPrefix = loc.id === 'en' ? '' : '/' + loc.id;
            const newUrl = window.location.origin + newPrefix + (path.startsWith('/') ? path : '/' + path);
            window.location.href = newUrl;
        };
        menu.appendChild(item);
    });

    trigger.onclick = (e) => {
        e.stopPropagation();
        container.classList.toggle('open');
    };

    // Close when clicking outside
    document.addEventListener('click', () => {
        container.classList.remove('open');
    });

    container.appendChild(trigger);
    container.appendChild(menu);
    parentEl.appendChild(container);
}

// Render Share Section with QR Code and App Links
export async function renderShareSection(parentEl, groupId, receiptId = null) {
    const { generateQRCodeSVG } = await import('./receiptDrawer.mjs');

    const shareContainer = document.createElement('div');
    shareContainer.className = 'share-section';
    shareContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;

        padding: 32px 16px;
    `;

    // QR Code
    let baseUrl = `${window.location.origin}/home/${groupId}`;
    if (receiptId) {
        baseUrl += `/receipt/${receiptId}`;
    }
    const qrUrl = `${baseUrl}?ref=qr`;
    const shareUrl = `${baseUrl}?ref=share`;

    const qrContainer = document.createElement('div');
    qrContainer.style.cssText = `
        display: inline-block;
        margin-bottom: 8px;
    `;
    qrContainer.innerHTML = generateQRCodeSVG(qrUrl);
    shareContainer.appendChild(qrContainer);


    // Share Link button
    const shareButton = document.createElement('div');
    shareButton.style.cssText = `
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: #6366f1;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        padding: 8px 16px;
        border-radius: 8px;
        transition: background-color 0.2s;
        margin-bottom: 24px;
    `;

    shareButton.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="18" cy="5" r="3"></circle>
            <circle cx="6" cy="12" r="3"></circle>
            <circle cx="18" cy="19" r="3"></circle>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
        <span>${t.shareLink}</span>
    `;

    shareButton.onmouseover = () => {
        shareButton.style.backgroundColor = '#f0f0f0';
    };

    shareButton.onmouseout = () => {
        shareButton.style.backgroundColor = 'transparent';
    };

    shareButton.onclick = async () => {
        const { trackEvent } = await import('./utils.mjs');
        trackEvent('share_link');

        // Try to use native share API if available
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Join my SplitPal group',
                    text: 'Join my expense group on SplitPal',
                    url: shareUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Share failed:', err);
                    copyToClipboard(shareUrl, shareButton);
                }
            }
        } else {
            // Fallback to copy to clipboard
            copyToClipboard(shareUrl, shareButton);
        }
    };

    shareContainer.appendChild(shareButton);
    parentEl.appendChild(shareContainer);
}

// Helper function to copy to clipboard
function copyToClipboard(text, buttonEl) {
    navigator.clipboard.writeText(text).then(() => {
        const originalHTML = buttonEl.innerHTML;
        buttonEl.innerHTML = `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>${t.done}!</span>
        `;
        setTimeout(() => {
            buttonEl.innerHTML = originalHTML;
        }, 2000);
    }).catch(err => {
        console.error('Failed to copy:', err);
        alert('Failed to copy link');
    });
}



// Export renderReceiptView for compatibility with other modules
export { renderReceiptView };

// Get DOM elements
export function getElements() {
    return {
        messagesEl: document.getElementById('messages'),
        connectBtn: document.getElementById('connectBtn'),
        disconnectBtn: document.getElementById('disconnectBtn'),
        subscriptionTypeEl: document.getElementById('subscriptionType'),
        groupIdContainerEl: document.getElementById('groupIdContainer'),
        groupIdEl: document.getElementById('groupId'),
        clearBtn: document.getElementById('clearBtn')
    };
}

// Render Modal Bottom Sheet
export function renderModal({
    anchorEl,
    body,
    buttons,
    title,
    onClose
}) {
    // Create overlay (container)
    const overlay = document.createElement('div');
    overlay.className = 'bottom-sheet-overlay';

    // Create sheet content
    const content = document.createElement('div');
    content.className = 'bottom-sheet-content';

    // --- Header Section ---
    const headerSection = document.createElement('div');
    headerSection.className = 'sheet-header-section';

    // Handle Bar
    const handle = document.createElement('div');
    handle.className = 'sheet-handle-bar';
    headerSection.appendChild(handle);

    // Back Button (shown only on mobile)
    const backBtn = document.createElement('button');
    backBtn.className = 'sheet-back-btn';
    backBtn.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>`;
    backBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closeSheet();
    });
    headerSection.appendChild(backBtn);

    // Title
    if (title) {
        const modalTitle = document.createElement('div');
        modalTitle.className = 'modal-title';
        if (typeof title === 'string') {
            modalTitle.textContent = title;
        } else if (title instanceof HTMLElement) {
            modalTitle.appendChild(title);
        }
        headerSection.appendChild(modalTitle);
    }

    content.appendChild(headerSection);

    // --- Scrollable Body ---
    const scrollBody = document.createElement('div');
    scrollBody.className = 'sheet-scroll-body';

    // Body Content
    if (body) {
        const bodyWrapper = document.createElement('div');
        bodyWrapper.className = 'modal-body';
        if (typeof body === 'string') {
            bodyWrapper.innerHTML = body;
        } else if (body instanceof HTMLElement) {
            bodyWrapper.appendChild(body);
        }
        scrollBody.appendChild(bodyWrapper);
    }

    // Buttons
    if (buttons && buttons.length > 0) {
        const modalButtons = document.createElement('div');
        modalButtons.className = 'modal-buttons';
        // Add some spacing for the sheet look
        modalButtons.style.marginTop = '20px';
        modalButtons.style.paddingBottom = '10px'; // Extra safe areas

        buttons.forEach(button => {
            const btn = document.createElement('button');
            btn.textContent = button.text;
            btn.className = button.className || 'modal-button';

            btn.addEventListener('click', (e) => {
                if (button.onClick) {
                    button.onClick(e);
                }
                // Close modal after button click unless explicitly set to false
                if (button.closeOnClick !== false) {
                    closeSheet();
                }
            });
            modalButtons.appendChild(btn);
        });
        scrollBody.appendChild(modalButtons);
    }

    content.appendChild(scrollBody);
    overlay.appendChild(content);

    // Append to document body (overlay is fixed)
    document.body.appendChild(overlay);

    // Prevent background scrolling
    document.body.style.overflow = 'hidden';

    // --- Animation & Logic ---

    // Show animation
    requestAnimationFrame(() => {
        overlay.classList.add('show');
    });

    // Close Logic
    const closeSheet = () => {
        document.body.style.overflow = ''; // Restore scroll
        overlay.classList.remove('show');
        content.style.transform = 'translateX(-50%) translateY(100%)';

        // Call onClose callback if provided
        if (onClose) {
            onClose();
        }

        setTimeout(() => {
            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
        }, 300);
    };

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            closeSheet();
        }
    });



    return overlay;
}

export function closeModal(modalContainer) {
    if (modalContainer && modalContainer.classList.contains('bottom-sheet-overlay')) {
        // It's a sheet
        document.body.style.overflow = '';
        modalContainer.classList.remove('show');
        const content = modalContainer.querySelector('.bottom-sheet-content');
        if (content) content.style.transform = 'translateX(-50%) translateY(100%)';
        setTimeout(() => {
            if (modalContainer.parentNode) modalContainer.remove();
        }, 300);
    } else if (modalContainer) {
        // Fallback for old modals if any exist (unlikely)
        modalContainer.classList.remove('show');
        setTimeout(() => {
            if (modalContainer.parentNode) modalContainer.remove();
        }, 300);
    }
}

/**
 * Renders an error screen in the main app container.
 * @param {string} message - The error message to display.
 */
export function renderError(message) {
    const el = document.getElementById('app-container');
    if (!el) return;

    // Map common technical errors to user-friendly messages
    let userMessage = message;
    if (message === 'group not found') {
        userMessage = t.pageNotFound;
    }

    el.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; text-align: center; height: 60vh;">
            <div style="width: 80px; height: 80px; background-color: #FEE2E2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 24px;">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#DC2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            </div>
            <h2 style="font-size: 24px; font-weight: 700; color: #111827; margin-bottom: 12px;">${t.oopsSomethingWentWrong}</h2>
            <p style="font-size: 16px; color: #6B7280; margin-bottom: 32px; max-width: 320px;">${userMessage || t.unknownErrorHasOccured}</p>
            <button id="error-home-btn" style="background-color: var(--color-primary); color: white; border: none; padding: 12px 32px; border-radius: 100px; font-weight: 600; cursor: pointer; font-size: 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: transform 0.1s, opacity 0.2s;">
                ${t.goToHome}
            </button>
        </div>
    `;

    const homeBtn = document.getElementById('error-home-btn');
    if (homeBtn) {
        homeBtn.onmouseenter = () => { homeBtn.style.opacity = '0.9'; };
        homeBtn.onmouseleave = () => { homeBtn.style.opacity = '1'; };
        homeBtn.onmousedown = () => { homeBtn.style.transform = 'scale(0.98)'; };
        homeBtn.onmouseup = () => { homeBtn.style.transform = 'scale(1)'; };
        homeBtn.onclick = () => {
            import('./router.mjs').then(module => {
                module.default.navigate('/home');
            });
        };
    }
}
